import React, {
  useContext,
  type FC,
  memo,
  forwardRef,
  type KeyboardEventHandler,
  type ChangeEventHandler,
  type RefObject,
} from 'react';
import { AppStateContext } from '../../../contexts/AppState';
import { DeviceTypeContext } from '../../../contexts/DeviceType';
import { type DeviceType } from '../../../types/DeviceType';
import { type GeneralFieldProps } from '../../../types/Field';
import { FieldName } from '../../../types/CardFieldName';
import { t } from '../../../utils/i18n';
import { formatCardExpiry } from '../../CardForm/utils/formatting';
import { FieldErrorMessage } from '../../FieldErrorMessage';
import { FieldInput } from '../../FieldInput';
import { FieldLabel } from '../../FieldLabel';
import { getFieldErrorMessage } from '../../../utils/getFieldErrorMessage';
import { DH } from '../../../utils/DataHook';
import { maxExpireInputSize } from './constants';
import { Container } from './elements';

interface CardExpireDateViewProps extends Omit<GeneralFieldProps, 'onChange'> {
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onChange: ChangeEventHandler<HTMLInputElement>;
}

export const CardExpireDateView: FC<CardExpireDateViewProps> = memo(
  forwardRef((props: CardExpireDateViewProps, ref: RefObject<HTMLInputElement>) => {
    const {
      isTouched,
      isFinished,
      value,
      isValid,
      isErrorCanBeShown,
      customInput,

      onKeyDown,
      onChange,
      onBlur,
      onFocus,
      onLabelClick,
    } = props;
    const formattedValue: string = formatCardExpiry(value);
    const { isSavedCard } = useContext(AppStateContext);
    const deviceType: DeviceType = useContext(DeviceTypeContext);

    if (!customInput) {
      return (
        <div data-hook={DH.Field.Container.Expiration}>
          <FieldInput
            ref={ref}
            fieldName={FieldName.ExpireDate}
            label={t('card.expireDate')}
            maxLength={maxExpireInputSize}
            placeholder={t('card.expireDate.placeholder.redesign')}
            type="tel"
            value={formattedValue}
            required
            isTouched={isTouched}
            isValid={isValid}
            isErrorCanBeShown={isErrorCanBeShown}
            errorMessage={getFieldErrorMessage({ fieldName: FieldName.ExpireDate, isFinished, isEmpty: !value })}
            autoComplete="cc-exp"
            onKeyDown={onKeyDown}
            onChangeRaw={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>
      );
    }

    return (
      <Container isLimitedWidth={isSavedCard && deviceType === 'desktop'}>
        <FieldLabel fieldName={FieldName.ExpireDate} text={t('card.expireDate')} onClick={onLabelClick} />
        <FieldInput
          ref={ref}
          customInput
          fieldName={FieldName.ExpireDate}
          size={maxExpireInputSize}
          maxLength={maxExpireInputSize}
          placeholder={t('card.expireDate.placeholder')}
          type="tel"
          value={formattedValue}
          isValid={isValid}
          isTouched={isTouched}
          required
          name="exp-date"
          autoComplete="cc-exp"
          onKeyDown={onKeyDown}
          onChangeRaw={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {isTouched && !isValid && <FieldErrorMessage fieldName={FieldName.ExpireDate} />}
      </Container>
    );
  }),
);
CardExpireDateView.displayName = 'CardExpireDateView';
